import type { BoroughDeliveryEstimate } from "@/lib/storefront-types";

type DeliveryEstimatorSliderLike = {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
};

const FALLBACK_MESSAGE = "Delivery estimates are not available for this area right now.";
const FALLBACK_CURRENCY = "GBP";
const FALLBACK_STEP = 5;

export function buildBoroughDeliveryEstimateCacheKey(path: string, spend: number) {
  return `${path}:${spend.toFixed(2)}`;
}

export function clampSpendToSlider(spend: number, slider: DeliveryEstimatorSliderLike) {
  const boundedMax = Math.max(slider.min, slider.max);
  const clampedValue = Math.min(Math.max(spend, slider.min), boundedMax);
  const step = slider.step > 0 ? slider.step : FALLBACK_STEP;
  const relativeValue = clampedValue - slider.min;
  const roundedSteps = Math.round(relativeValue / step);
  const roundedValue = slider.min + roundedSteps * step;

  return Math.min(Math.max(roundedValue, slider.min), boundedMax);
}

export function createUnavailableBoroughDeliveryEstimate(
  path: string,
  message = FALLBACK_MESSAGE,
  spend = 0,
) {
  const normalizedSpend = Math.max(0, spend);

  return {
    path,
    currency: FALLBACK_CURRENCY,
    spend: normalizedSpend,
    slider: {
      min: 0,
      max: 0,
      step: FALLBACK_STEP,
      defaultValue: 0,
    },
    options: [],
    message: message.trim() || FALLBACK_MESSAGE,
  };
}

/**
 * Ensures each option carries a `freeDeliveryThreshold` (the basket value at
 * which its fee reaches £0). Prefers the CMS-provided value; when absent (an
 * older CMS), it's derived from the linear subsidy model using two price points
 * we already have server-side — the base estimate at £0 spend (= the un-subsidised
 * quote) and the current estimate's price at its spend. Returns a new estimate;
 * options that can't be derived are left untouched.
 */
export function deriveFreeDeliveryThresholds(
  currentEstimate: BoroughDeliveryEstimate,
  baseEstimate: BoroughDeliveryEstimate,
): BoroughDeliveryEstimate {
  const step = currentEstimate.slider.step > 0 ? currentEstimate.slider.step : FALLBACK_STEP;
  const currentSpend = currentEstimate.spend;
  const basePriceByKey = new Map(
    baseEstimate.options.map((option) => [option.key, option.price]),
  );

  const options = currentEstimate.options.map((option) => {
    if (option.freeDeliveryThreshold != null) {
      return option; // CMS-provided value is authoritative.
    }

    const basePrice = basePriceByKey.get(option.key);
    // Need a positive un-subsidised quote, a positive spend to measure the
    // slope, and a still-positive (decreasing) current price to extrapolate.
    if (
      basePrice == null ||
      basePrice <= 0 ||
      currentSpend <= 0 ||
      option.price <= 0 ||
      option.price >= basePrice
    ) {
      return option;
    }

    const subsidyPerPound = (basePrice - option.price) / currentSpend;
    const rawThreshold = basePrice / subsidyPerPound;
    const freeDeliveryThreshold = Math.ceil(rawThreshold / step) * step;
    return { ...option, freeDeliveryThreshold };
  });

  return { ...currentEstimate, options };
}
